/**
 * SilkReels 后端 HTTP 客户端
 *
 * 对齐契约 `.cursor/API.md`:
 *   • 所有接口返回 `{ code: number; message?: string; data: T }`
 *   • code === 0 为成功,其余抛 `ApiError`
 *   • 鉴权头格式 `Authorization: Bearer <access_token>`
 *   • access token 过期时自动 refresh 并重试;refresh 失败跳转登录
 *
 * 不引入 axios,使用浏览器原生 fetch。
 */
import {
  AuthSessionExpiredError,
  ensureFreshAccessToken,
  handleAuthFailure,
  readAccessToken,
  refreshAuthSession,
} from './authSession'
import { SILKREELS_API_BASE, TENANT_HEADER } from './config'
import { extractSuspendedPayload, TENANT_SUSPENDED_BIZ_CODE } from '@/shared/lib/tenant-suspended'
import { getActiveTenantCode } from '@/shared/lib/tenant-code'
import { normalizeUiLang, uiLangToApiLocale } from '@/i18n/locale-config'

export interface ApiResponse<T> {
  code: number
  message?: string
  data: T
}

/** 业务错误,code 段位参考 `.cursor/API.md` */
export class ApiError extends Error {
  readonly code: number
  readonly status: number
  readonly payload: unknown
  readonly traceId?: string
  readonly suspended?: import('@/shared/lib/tenant-suspended').TenantSuspendedPayload

  constructor(opts: {
    code: number
    status: number
    message: string
    payload?: unknown
    traceId?: string
    suspended?: import('@/shared/lib/tenant-suspended').TenantSuspendedPayload
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.code = opts.code
    this.status = opts.status
    this.payload = opts.payload
    this.traceId = opts.traceId
    this.suspended = opts.suspended
  }

  /** 401 或业务 40100~40199 视为鉴权失败 */
  isAuthError(): boolean {
    return this.status === 401 || (this.code >= 40100 && this.code <= 40199)
  }
}

export { readAccessToken }

const DEFAULT_TIMEOUT_MS = 15_000

function requestSignal(userSignal?: AbortSignal): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(DEFAULT_TIMEOUT_MS)
  if (!userSignal) return timeoutSignal
  return AbortSignal.any([userSignal, timeoutSignal])
}

/** 从浏览器语言偏好映射到 API locale (BCP-47) */
export function resolveApiLocale(): string {
  if (typeof window === 'undefined') return 'zh-CN'
  const raw = window.localStorage.getItem('silkreels.lang') || navigator.language || 'zh'
  return uiLangToApiLocale(normalizeUiLang(raw))
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  headers?: Record<string, string>
  /** 是否带鉴权头,默认 true */
  auth?: boolean
  /** 跨域 cookie,默认 'include' 与 backend `AllowCredentials=true` 配合 */
  credentials?: RequestCredentials
  signal?: AbortSignal
  /** 内部:401 后是否尝试 refresh,默认 true */
  _retryAuth?: boolean
}

async function apiRequestOnce<T>(path: string, opts: RequestOptions): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    headers = {},
    auth = true,
    credentials = 'include',
    signal: userSignal,
  } = opts
  const signal = requestSignal(userSignal)

  let url = `${SILKREELS_API_BASE}${path}`
  if (query) {
    const qs = new URLSearchParams()
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null) qs.append(k, String(v))
    })
    const qsStr = qs.toString()
    if (qsStr) url += `?${qsStr}`
  }

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': resolveApiLocale(),
    [TENANT_HEADER]: getActiveTenantCode(),
    ...headers,
  }
  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] ?? 'application/json'
  }
  if (auth) {
    const token = readAccessToken()
    if (token) finalHeaders.Authorization = `Bearer ${token}`
  }

  const res = await fetch(url, {
    method,
    headers: finalHeaders,
    credentials,
    body:
      body === undefined
        ? undefined
        : body instanceof FormData
          ? body
          : JSON.stringify(body),
    signal,
  })

  let json: ApiResponse<T> | null = null
  const text = await res.text()
  if (text) {
    try {
      json = JSON.parse(text) as ApiResponse<T>
    } catch {
      /* 非 JSON 响应,例如 5xx 静态错误页 */
    }
  }

  if (!res.ok || !json) {
    throw new ApiError({
      code: json?.code ?? res.status,
      status: res.status,
      message: json?.message ?? res.statusText ?? 'request failed',
      payload: json,
      traceId: res.headers.get('x-trace-id') ?? undefined,
    })
  }
  if (json.code !== 0) {
    const suspended =
      json.code === TENANT_SUSPENDED_BIZ_CODE ? extractSuspendedPayload(json) ?? undefined : undefined
    throw new ApiError({
      code: json.code,
      status: res.status,
      message: json.message ?? 'business error',
      payload: json,
      traceId: res.headers.get('x-trace-id') ?? undefined,
      suspended,
    })
  }
  return json.data
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const { auth = true, _retryAuth = true } = opts

  if (auth) {
    await ensureFreshAccessToken()
  }

  try {
    return await apiRequestOnce<T>(path, opts)
  } catch (err) {
    if (auth && _retryAuth && err instanceof ApiError && err.isAuthError()) {
      const ok = await refreshAuthSession()
      if (ok) {
        return apiRequestOnce<T>(path, { ...opts, _retryAuth: false })
      }
      handleAuthFailure()
      throw new AuthSessionExpiredError()
    }
    throw err
  }
}

/** multipart / 二进制下载等不走 JSON 封装,但仍需 refresh + 401 重试 */
export async function authenticatedFetch(
  path: string,
  init: RequestInit = {},
  retryAuth = true,
): Promise<Response> {
  await ensureFreshAccessToken()

  const doFetch = () => {
    const headers = new Headers(init.headers)
    const token = readAccessToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    headers.set(TENANT_HEADER, getActiveTenantCode())
    if (!headers.has('Accept-Language')) {
      headers.set('Accept-Language', resolveApiLocale())
    }
    return fetch(`${SILKREELS_API_BASE}${path}`, {
      ...init,
      headers,
      credentials: init.credentials ?? 'include',
      signal: requestSignal(init.signal ?? undefined),
    })
  }

  let res = await doFetch()
  if (retryAuth && res.status === 401) {
    const ok = await refreshAuthSession()
    if (ok) {
      res = await doFetch()
    } else {
      handleAuthFailure()
      throw new AuthSessionExpiredError()
    }
  }
  return res
}
